import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Edit2, Trash2, ExternalLink, ShoppingBag, Save, Upload, ImageIcon, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import logger from "../../lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import VariationManager from "./VariationManager";

const EMPTY_FORM = {
  name: "",
  slug: "",
  url: "",
  description: "",
  price: "",
  custom_shipping_rate: "",
  low_stock_threshold: "5",
  images: [],
  visible: true,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

export default function AdminShop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);

  // Footer message (shop_footer_message in page_content)
  const [footerMessage, setFooterMessage] = useState("");
  const [savingFooter, setSavingFooter] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const data = await api.getAdminShopItems();
      setItems(data);
    } catch {
      toast.error("Failed to load shop items");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFooterMessage = useCallback(async () => {
    try {
      const content = await api.getContent("shop_footer_message");
      setFooterMessage(content.value || "Check back often — more official Demolition merchandise is being added all the time.");
    } catch {
      setFooterMessage("Check back often — more official Demolition merchandise is being added all the time.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, content] = await Promise.all([
          api.getAdminShopItems(),
          api.getContent("shop_footer_message").catch(() => ({ value: "" })),
        ]);
        if (cancelled) return;
        setItems(data);
        setFooterMessage(
          content.value || "Check back often — more official Demolition merchandise is being added all the time."
        );
      } catch {
        if (!cancelled) toast.error("Failed to load shop items");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      slug: item.slug || "",
      url: item.url,
      description: item.description || "",
      price: item.price != null ? String(item.price) : "",
      custom_shipping_rate: item.custom_shipping_rate != null ? String(item.custom_shipping_rate) : "",
      low_stock_threshold: String(item.low_stock_threshold ?? 5),
      images: item.images && item.images.length > 0 ? [...item.images] : (item.thumbnail_url ? [item.thumbnail_url] : []),
      visible: item.visible !== false,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingThumb(true);
    try {
      const uploads = await Promise.all(files.map((f) => api.uploadAdminImage(f)));
      const urls = uploads.map((r) => r.url).filter(Boolean);
      setFormData((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
    } catch (err) {
      logger.warn("Image upload failed:", err.message);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingThumb(false);
    }
  };

  const moveImage = (idx, direction) => {
    setFormData((prev) => {
      const next = [...prev.images];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= next.length) return prev;
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return { ...prev, images: next };
    });
  };

  const removeImage = (idx) => {
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const parseMoney = (v) => {
    const trimmed = (v || "").trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return NaN; // signal invalid
    return Math.round(n * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const price = parseMoney(formData.price);
    const customShipping = parseMoney(formData.custom_shipping_rate);
    if (Number.isNaN(price)) {
      toast.error("Price must be a positive number or blank.");
      return;
    }
    if (Number.isNaN(customShipping)) {
      toast.error("Custom shipping must be a positive number or blank.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        slug: (formData.slug || "").trim() || null,
        url: formData.url.trim(),
        description: formData.description.trim() || null,
        thumbnail_url: formData.images[0] || "",
        images: formData.images,
        price,
        custom_shipping_rate: customShipping,
        low_stock_threshold: Number(formData.low_stock_threshold) || 5,
        visible: !!formData.visible,
      };
      if (editingItem) {
        await api.updateShopItem(editingItem.id, payload);
        toast.success("Shop item updated");
      } else {
        await api.createShopItem(payload);
        toast.success("Shop item added");
      }
      setDialogOpen(false);
      fetchItems();
    } catch {
      toast.error("Failed to save shop item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.deleteShopItem(item.id);
      toast.success("Shop item deleted");
      fetchItems();
    } catch {
      toast.error("Failed to delete shop item");
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    // Optimistic UI: swap in local state immediately
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setItems(reordered);
    try {
      await api.reorderShopItems(reordered.map((it) => it.id));
    } catch (err) {
      logger.warn("Reorder failed:", err.message);
      toast.error("Couldn't save new order — refreshing.");
      fetchItems();
    }
  };

  const handleSaveFooter = async () => {
    setSavingFooter(true);
    try {
      await api.updateContent("shop_footer_message", footerMessage);
      toast.success("Footer message saved");
    } catch {
      toast.error("Failed to save footer message");
    } finally {
      setSavingFooter(false);
    }
  };

  let submitLabel;
  if (saving) submitLabel = "Saving...";
  else if (editingItem) submitLabel = "Update";
  else submitLabel = "Add";

  return (
    <div data-testid="admin-shop-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            SHOP
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Manage external merchandise links shown on the Shop page
          </p>
        </div>
        <button
          onClick={openCreateDialog}
          className="flex items-center gap-2 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors"
          data-testid="create-shop-item-btn"
        >
          <Plus className="w-5 h-5" />
          Add Item
        </button>
      </div>

      {/* Items List */}
      {loading && (
        <div className="text-center py-12">
          <p className="font-mono text-white/50 animate-pulse">Loading shop items...</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-4" data-testid="admin-shop-items-list">
          {items.map((item, index) => (
            <div
              key={item.id}
              className="bg-demolition-paper border border-white/10 p-6"
              data-testid={`admin-shop-item-${item.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  {item.thumbnail_url ? (
                    <img
                      src={resolveImage(item.thumbnail_url)}
                      alt={item.name}
                      className="w-16 h-16 flex-shrink-0 object-cover border border-white/10"
                      data-testid={`admin-shop-thumb-${item.id}`}
                    />
                  ) : (
                    <div className="w-16 h-16 flex-shrink-0 bg-demolition-subtle border border-white/10 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-teko text-xl text-white uppercase tracking-wide break-words">
                        {item.name}
                      </h3>
                      {item.price != null && Number(item.price) > 0 ? (
                        <span
                          className="inline-block px-2 py-0.5 bg-demolition-red text-white font-teko text-xs uppercase tracking-widest"
                          data-testid={`admin-shop-badge-${item.id}`}
                        >
                          ${Number(item.price).toFixed(2)} · Direct
                        </span>
                      ) : (
                        <span
                          className="inline-block px-2 py-0.5 border border-white/20 text-white/60 font-teko text-xs uppercase tracking-widest"
                          data-testid={`admin-shop-badge-${item.id}`}
                        >
                          Affiliate
                        </span>
                      )}
                      {item.custom_shipping_rate != null && Number(item.custom_shipping_rate) > 0 && (
                        <span className="inline-block px-2 py-0.5 border border-demolition-chrome/40 text-demolition-chrome font-mono text-[10px] tracking-widest">
                          SHIP ${Number(item.custom_shipping_rate).toFixed(2)}
                        </span>
                      )}
                      {item.visible === false && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 font-teko text-xs uppercase tracking-widest"
                          data-testid={`admin-shop-hidden-badge-${item.id}`}
                        >
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="font-mono text-white/60 text-sm mt-1">{item.description}</p>
                    )}
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 mt-2 text-demolition-red hover:underline font-mono text-sm break-all"
                      data-testid={`admin-shop-link-${item.id}`}
                    >
                      <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      {item.url}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex flex-col" data-testid={`reorder-shop-${item.id}`}>
                    <button
                      type="button"
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-white/50 hover:text-demolition-red hover:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                      data-testid={`move-up-shop-${item.id}`}
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(index, 1)}
                      disabled={index === items.length - 1}
                      className="p-1 text-white/50 hover:text-demolition-red hover:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                      data-testid={`move-down-shop-${item.id}`}
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => openEditDialog(item)}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                    data-testid={`edit-shop-item-${item.id}`}
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-2 text-white/50 hover:text-red-500 hover:bg-white/10 transition-colors"
                    data-testid={`delete-shop-item-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 bg-demolition-paper border border-white/10" data-testid="admin-shop-empty">
          <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="font-mono text-white/50">
            No shop items yet. Click &quot;Add Item&quot; to list your first vendor link.
          </p>
        </div>
      )}

      {/* Footer Message Editor */}
      <div className="mt-12 bg-demolition-paper border border-white/10 p-6" data-testid="shop-footer-editor">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <Label className="font-teko text-xl text-white uppercase tracking-wider">
              Footer Message
            </Label>
            <p className="font-mono text-white/40 text-sm mt-1">
              The message shown at the bottom of the Shop page, encouraging visitors to return.
            </p>
          </div>
          <button
            onClick={handleSaveFooter}
            disabled={savingFooter}
            className="flex items-center gap-2 px-4 py-2 bg-demolition-red text-white font-teko text-base uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50 flex-shrink-0"
            data-testid="save-shop-footer-btn"
          >
            <Save className="w-4 h-4" />
            {savingFooter ? "Saving..." : "Save"}
          </button>
        </div>
        <Textarea
          value={footerMessage}
          onChange={(e) => setFooterMessage(e.target.value)}
          rows={3}
          placeholder="Check back often — more official Demolition merchandise is being added all the time."
          className="bg-demolition-subtle border-white/10 text-white font-mono resize-none"
          data-testid="shop-footer-input"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-demolition-paper border-white/10 max-w-3xl max-h-[90vh] flex flex-col overflow-hidden p-0"
          data-testid="shop-item-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="font-russo text-2xl text-white uppercase tracking-tighter">
              {editingItem ? "EDIT SHOP ITEM" : "ADD SHOP ITEM"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="space-y-6 overflow-y-auto px-6 py-4 flex-1" data-testid="shop-item-dialog-body">
              <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Vendor / Site Name
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g. Pro Wrestling Tees"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                URL
              </Label>
              <Input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                placeholder="https://www.prowrestlingtees.com/demolitionaxandsmash"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                URL Slug (optional)
              </Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="auto-generated from name if blank"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-slug-input"
              />
              <p className="font-mono text-xs text-white/40">
                Used in the product URL: <code className="text-demolition-chrome">/shop/p/{formData.slug || "auto-from-name"}</code>
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Product Images
              </Label>
              {formData.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="shop-images-grid">
                  {formData.images.map((src, idx) => (
                    <div
                      key={`${src}-${idx}`}
                      className={`relative bg-demolition-subtle border ${idx === 0 ? "border-demolition-red" : "border-white/10"}`}
                      data-testid={`shop-image-${idx}`}
                    >
                      <img
                        src={resolveImage(src)}
                        alt={`Product ${idx + 1}`}
                        className="w-full aspect-square object-cover"
                      />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 bg-demolition-red text-white font-teko text-[10px] uppercase tracking-widest px-1.5 py-0.5">
                          Primary
                        </span>
                      )}
                      <div className="absolute top-1 right-1 flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => moveImage(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 bg-black/60 text-white hover:bg-black/80 disabled:opacity-30 transition-colors"
                          aria-label="Move up"
                          data-testid={`shop-image-up-${idx}`}
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 1)}
                          disabled={idx === formData.images.length - 1}
                          className="p-1 bg-black/60 text-white hover:bg-black/80 disabled:opacity-30 transition-colors"
                          aria-label="Move down"
                          data-testid={`shop-image-down-${idx}`}
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="p-1 bg-black/60 text-white hover:bg-red-600 transition-colors"
                          aria-label="Remove image"
                          data-testid={`shop-image-remove-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => thumbInputRef.current?.click()}
                disabled={uploadingThumb}
                className="w-full p-4 border-2 border-dashed border-white/20 hover:border-demolition-red/60 transition-colors flex flex-col items-center gap-2 text-white/50 hover:text-white disabled:opacity-50"
                data-testid="upload-shop-thumb-btn"
              >
                {uploadingThumb ? (
                  <p className="font-mono text-sm animate-pulse">Uploading...</p>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="font-mono text-sm">
                      {formData.images.length ? "Add more images" : "Click to upload images"}
                    </span>
                    <span className="font-mono text-xs text-white/30">First image is the primary thumbnail · JPG/PNG/GIF/WEBP · Multi-select supported</span>
                  </>
                )}
              </button>
              <input
                ref={thumbInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="shop-item-image-file-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Price (USD)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="Leave blank for affiliate-only items"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-price-input"
              />
              <p className="font-mono text-xs text-white/40">
                Set a price to enable direct sale via Stripe Checkout. Leave blank to keep this as an affiliate &quot;Visit&quot; link.
              </p>
            </div>

            {/* Visibility toggle — available for ALL items (direct-sale + affiliate) so admins can stage merch */}
            <div className="space-y-2" data-testid="shop-item-visibility-section">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Public Visibility
              </Label>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, visible: !formData.visible })}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-2 transition-colors text-left ${
                  formData.visible
                    ? "border-demolition-red/60 bg-demolition-red/5 hover:bg-demolition-red/10"
                    : "border-yellow-500/50 bg-yellow-500/5 hover:bg-yellow-500/10"
                }`}
                data-testid="shop-item-visibility-toggle"
                aria-pressed={formData.visible}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {formData.visible ? (
                    <Eye className="w-5 h-5 text-demolition-red flex-shrink-0" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-russo text-base text-white uppercase tracking-tight">
                      {formData.visible ? "Live — Publicly Visible" : "Hidden — Admin Only"}
                    </p>
                    <p className="font-mono text-xs text-white/40">
                      {formData.visible
                        ? "Fans can view this item on the Shop page."
                        : "Item is hidden from the public Shop page and product URL returns 404. Stage merch here and flip on when ready."}
                    </p>
                  </div>
                </div>
                <span
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 border-2 transition-colors ${
                    formData.visible
                      ? "bg-demolition-red border-demolition-red"
                      : "bg-transparent border-white/30"
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 bg-white transition-transform ${
                      formData.visible ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Custom Shipping (USD, optional)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={formData.custom_shipping_rate}
                onChange={(e) =>
                  setFormData({ ...formData, custom_shipping_rate: e.target.value })
                }
                placeholder="Leave blank to use the $5.99 base shipping"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-shipping-input"
              />
              <p className="font-mono text-xs text-white/40">
                If this item costs more to ship than $5.99, set the override here. The cart applies the highest override across all items in the order.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Low Stock Threshold
              </Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={formData.low_stock_threshold}
                onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                placeholder="5"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="shop-item-low-stock-input"
              />
              <p className="font-mono text-xs text-white/40">
                When a variation&apos;s inventory is at or below this number, the product page will show a &quot;Low Stock&quot; warning to fans.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Description (Optional)
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Short description of the vendor or product line..."
                className="bg-demolition-subtle border-white/10 text-white font-mono resize-none"
                data-testid="shop-item-description-input"
              />
            </div>

            {editingItem && (
              <VariationManager
                itemId={editingItem.id}
                lowStockThreshold={Number(formData.low_stock_threshold) || 5}
                onChange={fetchItems}
              />
            )}
            {!editingItem && (
              <p className="font-mono text-xs text-white/40 border border-white/10 p-3">
                Save the product first, then re-open it to add size / color / style variations and inventory.
              </p>
            )}
            </div>

            <DialogFooter className="gap-3 border-t border-white/10 px-6 py-4 flex-shrink-0 bg-demolition-paper">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="px-6 py-2 border border-white/20 text-white/70 font-teko text-lg uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
                data-testid="save-shop-item-btn"
              >
                {submitLabel}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
